<template>
  <div
    class="grid grid-cols-2 gap-4 bg-base-200 rounded-xl p-4 text-xs claim-list"
  >
    <MineClaimBox
      :dtLastClaim="userStore.user.dtLastClaimChest!"
      :type="ChestType.Normal"
      :rarity="EquipmentRarity.Common"
      @equipment-claimed="onEquipmentClaimed"
    />

    <div
      :class="[
        'flex flex-col gap-1 text-right',
        userStore.user.claimedChestCount < 5 && 'opacity-50',
      ]"
    >
      <div class="flex gap-1 items-center justify-end">
        <img
          src="/img/energy-box-2.png"
          alt=""
          class="w-4 h-4 img-render-nearest"
        />
        <div class="font-bold rarity-2">
          {{ $t("User.rareEnergyBox") }}
        </div>
      </div>

      <div v-if="userStore.user.claimedChestCount < 5">
        {{
          $t("User.rareEnergyBoxProgress", {
            count: userStore.user.claimedChestCount,
          })
        }}
      </div>
      <div v-else>
        <button class="btn btn-xs btn-primary" @click="clickClaimChest()">
          {{ $t("Buttons.claim") }}
        </button>
      </div>
    </div>

    <MineClaimBox
      v-if="userStore.isBasicMember"
      :dtLastClaim="userStore.user.dtLastClaimBasicMemberChest!"
      :type="ChestType.BasicMember"
      @equipment-claimed="onEquipmentClaimed"
    />

    <MineClaimBox
      v-if="userStore.isStandardMember"
      :dtLastClaim="userStore.user.dtLastClaimStandardMemberChest!"
      :type="ChestType.StandardMember"
      @equipment-claimed="onEquipmentClaimed"
    />

    <MineClaimBox
      v-if="userStore.isPremiumMember"
      :dtLastClaim="userStore.user.dtLastClaimPremiumMemberChest!"
      :type="ChestType.PremiumMember"
      @equipment-claimed="onEquipmentClaimed"
    />

    <EquipmentDetailModal
      v-model:show="showEquipmentModal"
      :equipment="activeEquipment"
      mode="edit"
      show-gain-title
    />
  </div>
</template>

<script lang="ts" setup>
import { EquipmentRarity } from "@/enums/mission";
import api from "@/modules/api";
import { useGlobalUser } from "@/stores/globalUser";
import { MateEquipment } from "@/types/entity";
import { AxiosError } from "axios";
import { showConfirmDialog, showLoadingToast } from "vant";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { ChestType } from "@/types/user";

const userStore = useGlobalUser();
const { t } = useI18n();
const router = useRouter();

const showEquipmentModal = ref(false);
const activeEquipment = ref<MateEquipment>();

const clickClaimChest = async () => {
  const loading = showLoadingToast({
    message: t("Message.loading"),
    duration: 0,
    forbidClick: true,
  });

  try {
    const res = await api.claimChest(ChestType.Normal, EquipmentRarity.Rare);
    await userStore.refresh();
    loading.close();
    onEquipmentClaimed(res.itemzz[0]);
    userStore.user.claimedChestCount = 0;
  } catch (e: unknown) {
    console.log(e);
    const error = e as AxiosError<{ message: string }>;
    const message = error.response?.data.message;
    if (
      message?.startsWith("装备背包容量不足") ||
      message?.startsWith("Not enough equipment inventory space")
    ) {
      showConfirmDialog({
        title: t("Message.notEnoughEquipmentInventorySpace"),
        message: t("Message.notEnoughEquipmentInventorySpaceDesc"),
        confirmButtonText: t("Buttons.confirm"),
        cancelButtonText: t("Buttons.cancel"),
      }).then(() => {
        router.push({
          name: "missionEquipment",
        });
      });
    }

    loading.close();
  }
};

const onEquipmentClaimed = (equipment: MateEquipment) => {
  activeEquipment.value = equipment;
  showEquipmentModal.value = true;
};
</script>

<style lang="postcss">
.claim-list > div:nth-child(even) {
  @apply items-end;
}
</style>
